/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { PayloadAction } from "@reduxjs/toolkit";
import {
  IConfigStatus,
  IDeviceConfigInitialState,
  IIPAction,
  IIPInterface,
  ILanDevice,
  IRfFeed,
  IZoneDevice,
  IltepeerInterfaces,
  IngpeerInterfaces,
} from "./types";
import { OPERATIONTYPES } from "../../../globals/constants";

export const setLanDeviceListAction = (state: IDeviceConfigInitialState, action: PayloadAction<ILanDevice[]>) => {
  state.lanDeviceList = action.payload;
};

export const setZoneDeviceListAction = (state: IDeviceConfigInitialState, action: PayloadAction<IZoneDevice[]>) => {
  state.zoneDeviceList = action.payload;
};

export const setIPInterfaceListAction = (state: IDeviceConfigInitialState, action: PayloadAction<IIPAction>) => {
  let { lanDeviceList } = state;
  const { ipInterfaceData, operationType } = action.payload;
  if (!!ipInterfaceData?.vlanId) {
    const indexOfIPInterface = lanDeviceList.findIndex((item) => item.index === ipInterfaceData?.vlanId);
    if (operationType === OPERATIONTYPES.CREATE) {
      if (lanDeviceList[indexOfIPInterface].ipInterfaces != null) {
        lanDeviceList[indexOfIPInterface].ipInterfaces?.push(ipInterfaceData);
      } else {
        lanDeviceList[indexOfIPInterface].ipInterfaces = [ipInterfaceData];
      }
    } else if (operationType === OPERATIONTYPES.UPDATE) {
      const toRemovedIP = lanDeviceList[indexOfIPInterface]?.ipInterfaces?.filter((item) => item.index !== ipInterfaceData?.index);
      if (lanDeviceList[indexOfIPInterface].ipInterfaces != null) {
        lanDeviceList[indexOfIPInterface].ipInterfaces = toRemovedIP;
      } else {
        lanDeviceList[indexOfIPInterface].ipInterfaces = toRemovedIP;
      }
    }
  }
};

export const setSelectedLanDeviceAction = (state: IDeviceConfigInitialState, action: PayloadAction<ILanDevice>) => {
  state.selectedLanDevice = action.payload;
};

export const setSelectedZoneDeviceAction = (state: IDeviceConfigInitialState, action: PayloadAction<IZoneDevice>) => {
  state.selectedZoneDevice = action.payload;
};

export const setIsAddNewLanDeviceAction = (state: IDeviceConfigInitialState, action: PayloadAction<boolean>) => {
  state.isAddNewLanDevice = action.payload;
  if (action.payload && !!state.selectedLanDevice) {
    state.selectedLanDevice.enable = "true";
    state.selectedLanDevice.index = "";
    state.selectedLanDevice.name = "";
    state.selectedLanDevice.maxBitRate = "1000";
    state.selectedLanDevice.duplex = "Full";
    state.selectedLanDevice.ipInterfaces = [];
  }
};

export const setIsAddNewZoneDeviceAction = (state: IDeviceConfigInitialState, action: PayloadAction<boolean>) => {
  state.isAddNewZoneDevice = action.payload;
  if (action.payload && !!state.selectedLanDevice) {
    state.selectedZoneDevice.index = "";
    state.selectedZoneDevice.cellList = [];
    state.selectedZoneDevice.scSpacingCommon = "";
    state.selectedZoneDevice.description = "";
    state.selectedZoneDevice.nrcell = 0;
    state.selectedZoneDevice.barringforemergency = false;
    state.selectedZoneDevice.eARFCNDL = "";
    state.selectedZoneDevice.ulBandwidth = "";
    state.selectedZoneDevice.eARFCNUL = "";
    state.selectedZoneDevice.dlBandwidth = "";
  }
};

export const setIsZoneAddedAction = (state: IDeviceConfigInitialState, action: PayloadAction<boolean>) => {
  state.isZoneAdded = action.payload;
};

export const setSelectedIPInterfaceAction = (state: IDeviceConfigInitialState, action: PayloadAction<IIPInterface>) => {
  state.selectedIPInterface = action.payload;
};

export const setIsAddNewIPInterfaceAction = (state: IDeviceConfigInitialState, action: PayloadAction<boolean>) => {
  state.isAddNewIPInterface = action.payload;
  if (action.payload && !!state.selectedIPInterface) {
    state.selectedIPInterface.description = "";
    state.selectedIPInterface.dhcpServerEnable = "false";
    state.selectedIPInterface.egressQosGroup = "";
    state.selectedIPInterface.forwardingGroupIndex = "";
    state.selectedIPInterface.index = "";
    state.selectedIPInterface.ipInterfaceIpAddress = "";
    state.selectedIPInterface.ipInterfaceSubnetMask = "";
    state.selectedIPInterface.operState = "";
    state.selectedIPInterface.vlanId = "";
    state.selectedIPInterface.enable = "false";
    if (!!state.selectedIPInterface?.stats) {
      state.selectedIPInterface.stats.bytesReceived = "";
      state.selectedIPInterface.stats.bytesSent = "";
      state.selectedIPInterface.stats.index = "";
      state.selectedIPInterface.stats.packetsReceived = "";
      state.selectedIPInterface.stats.packetsSent = "";
    }
  }
};

export const setLANDeviceIndexesAction = (state: IDeviceConfigInitialState, action: PayloadAction<Array<string>>) => {
  state.lanDeviceIndexes = action.payload;
};

export const setRfFeedIndexesAction = (state: IDeviceConfigInitialState, action: PayloadAction<Array<string>>) => {
  state.rfFeedIndexes = action.payload;
};

export const setZoneIndexesAction = (state: IDeviceConfigInitialState, action: PayloadAction<Array<string>>) => {
  state.zoneCellIndexes = action.payload;
};

export const setConfigCountsAction = (state: IDeviceConfigInitialState, action: PayloadAction<{ [key: string]: number }>) => {
  if (Object.values(action.payload).length > 0) {
    state.configCounts = { ...state.configCounts, ...action.payload };
  } else {
    state.configCounts = {};
  }
};

export const setConfigStatusDataAction = (state: IDeviceConfigInitialState, action: PayloadAction<IConfigStatus[]>) => {
  state.configStatusData = action.payload;
};

export const setRfFeedListAction = (state: IDeviceConfigInitialState, action: PayloadAction<IRfFeed[]>) => {
  state.rfFeedList = action.payload;
};

export const setSelectedRfFeedAction = (state: IDeviceConfigInitialState, action: PayloadAction<IRfFeed>) => {
  state.selectedRfFeed = action.payload;
};

export const setIsAddNewRfFeedAction = (state: IDeviceConfigInitialState, action: PayloadAction<boolean>) => {
  state.isAddNewRfFeed = action.payload;
  if (action.payload && !!state.selectedRfFeed) {
    state.selectedRfFeed.index = "";
    state.selectedRfFeed.type = "";
    state.selectedRfFeed.dlarfcn = "";
    state.selectedRfFeed.ularfcn = "";
    state.selectedRfFeed.dlBandwidth = "";
    state.selectedRfFeed.ulBandwidth = "";
    state.selectedRfFeed.txPowerPercentage = "";
    //state.selectedRfFeed.dlAttenuation = "";
  }
};

export const setLteX2ConnectionListAction = (state: IDeviceConfigInitialState, action: PayloadAction<IltepeerInterfaces[]>) => {
  state.lteX2ConnectionList = action.payload;
};

export const setSelectedLteX2ConnectionAction = (state: IDeviceConfigInitialState, action: PayloadAction<IltepeerInterfaces>) => {
  state.selectedLteX2Connection = action.payload;
};

export const setIsAddNewLteX2ConnectionAction = (state: IDeviceConfigInitialState, action: PayloadAction<boolean>) => {
  state.isAddNewLteX2Connection = action.payload;
  if (action.payload && !!state.selectedLteX2Connection) {
    state.selectedLteX2Connection.index = "";
    state.selectedLteX2Connection.enable = "";
    state.selectedLteX2Connection.name = "";
    state.selectedLteX2Connection.address = "";
    state.selectedLteX2Connection.connectionState = "";
    state.selectedLteX2Connection.localAddress = "";
    state.selectedLteX2Connection.plmnId = "";
    state.selectedLteX2Connection.port = "";
    state.selectedLteX2Connection.tac = "";
    state.selectedLteX2Connection.eNodeBID = "";
    state.selectedLteX2Connection.gNodeBID = "";
    state.selectedLteX2Connection.remoteAddress = "";
    state.selectedLteX2Connection.connectionId = "";
    state.selectedLteX2Connection.isENDCX2 = "";
  }
};

export const setLteX2ConnectionIndexesAction = (state: IDeviceConfigInitialState, action: PayloadAction<Array<string>>) => {
  state.lteX2ConnectionIndexes = action.payload;
};

export const setNgX2ConnectionListAction = (state: IDeviceConfigInitialState, action: PayloadAction<IngpeerInterfaces[]>) => {
  state.ngX2ConnectionList = action.payload;
};

export const setSelectedNgX2ConnectionAction = (state: IDeviceConfigInitialState, action: PayloadAction<IngpeerInterfaces>) => {
  state.selectedNgX2Connection = action.payload;
};

export const setIsAddNewNgX2ConnectionAction = (state: IDeviceConfigInitialState, action: PayloadAction<boolean>) => {
  state.isAddNewNgX2Connection = action.payload;
  if (action.payload && !!state.selectedNgX2Connection) {
    state.selectedNgX2Connection.index = "";
    state.selectedNgX2Connection.enable = "";
    state.selectedNgX2Connection.name = "";
    state.selectedNgX2Connection.address = "";
    state.selectedNgX2Connection.connectionState = "";
    state.selectedNgX2Connection.localAddress = "";
    state.selectedNgX2Connection.plmnId = "";
    state.selectedNgX2Connection.port = "";
    state.selectedNgX2Connection.eNodeBID = "";
    state.selectedNgX2Connection.remoteAddress = "";
    state.selectedNgX2Connection.connectionId = "";
  }
};

export const setNgX2ConnectionIndexesAction = (state: IDeviceConfigInitialState, action: PayloadAction<Array<string>>) => {
  state.ngX2ConnectionIndexes = action.payload;
};

export const setRadioUnitIndexesAction = (state: IDeviceConfigInitialState, action: PayloadAction<Array<string>>) => {
  state.radioUnitIndexes = action.payload;
};
